import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { OFFICES, FIRM_PHONE, PEAK_PHONE, MAIN_OFFICE_PHONE, FIRM_DISCIPLINES, PRACTICE_AREAS } from "@/lib/content";
import SEO from "@/components/SEO";
import { locationSchema, breadcrumbSchema } from "@/lib/schemas";

export default function Location() {
  const { slug } = useParams();
  const office = OFFICES.find((o) => o.slug === slug);

  if (!office) return <Navigate to="/locations" replace />;

  const otherOffices = OFFICES.filter((o) => o.slug !== slug);

  return (
    <div data-testid={`location-page-${office.slug}`}>
      <SEO
        title={office.seoTitle || `${office.region} Office — Texas Business, Wealth & Legacy Counsel`}
        description={office.seoDescription || `Lone Star Counsel in ${office.line2.replace(/,\s*Texas/, ", Texas")}. Serving ${office.serves}. ${office.headline}`}
        keywords={office.seoKeywords}
        path={`/locations/${office.slug}`}
        ogImage={office.hero}
        schema={[
          locationSchema(office),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Locations", path: "/locations" },
            { name: office.region, path: `/locations/${office.slug}` },
          ]),
        ]}
      />
      {/* HERO */}
      <section className="relative min-h-[88vh] flex flex-col justify-end overflow-hidden panel-navy" data-testid="location-hero">
        <div className="absolute inset-0">
          <img
            src={office.hero}
            alt={office.heroAlt}
            className="w-full h-full object-cover kenburns opacity-55"
            draggable={false}
          />
        </div>
        <div className="absolute inset-0 bg-navy/40 z-10 pointer-events-none"></div>
        <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-b from-transparent via-navy/55 to-navy z-10 pointer-events-none"></div>
        <div className="grain grain-dark absolute inset-0 z-10 pointer-events-none"></div>

        <div className="relative max-w-[1600px] mx-auto w-full px-6 md:px-12 pb-20 md:pb-28 pt-40">
          <div className="kicker kicker-ivory" data-testid="location-kicker">{office.kicker}</div>
          <div className="mt-4 text-[10px] uppercase tracking-[0.36em] text-brass/90">
            {FIRM_DISCIPLINES}
          </div>
          <h1 className="mt-8 font-display text-5xl md:text-7xl lg:text-8xl text-ivory leading-[0.95] max-w-5xl" style={{ textShadow: "0 2px 20px rgba(0,0,0,0.55)" }}>
            {office.headline}
            <span className="block italic font-cormorant text-brass mt-2">{office.italic}</span>
          </h1>
          <p className="mt-10 max-w-2xl text-lg text-ivory/85 font-light leading-relaxed">
            {office.clientele[0]}
          </p>

          <div className="mt-12 flex flex-wrap gap-4">
            <Link to="/intake" className="btn-brass" data-testid="location-cta-intake">
              Request a Conference <ArrowUpRight className="inline ml-2" size={18} />
            </Link>
            <a href={FIRM_PHONE.href} className="btn-ghost !border-ivory !text-ivory hover:!bg-ivory hover:!text-navy" data-testid="location-cta-phone">
              {FIRM_PHONE.display}
            </a>
          </div>
        </div>
      </section>

      {/* CLIENTELE + ADDRESS */}
      <section className="bg-ivory" data-testid="location-clientele">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-7">
              <div className="kicker">The Clientele</div>
              <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.05]">
                Who we counsel<br/>
                <span className="italic font-cormorant text-oxblood">in {office.region}.</span>
              </h2>
              {office.clientele.slice(1).map((p, i) => (
                <p key={i} className="mt-8 text-lg text-slate2 font-light leading-relaxed max-w-2xl" data-testid={`location-clientele-${i}`}>
                  {p}
                </p>
              ))}

              <div className="mt-14 hairline pt-8">
                <div className="kicker">Concentrations · {office.region}</div>
                <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 max-w-2xl">
                  {office.sectors.map((s, i) => (
                    <li key={i} className="font-serif text-lg text-navy flex items-baseline gap-3" data-testid={`location-sector-${i}`}>
                      <span className="text-brass font-cormorant italic text-base">{String(i + 1).padStart(2, "0")}</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <aside className="lg:col-span-4 lg:col-start-9">
              <div className="hairline pt-8 sticky top-32">
                <div className="kicker">The Office</div>
                <div className="mt-6 flex items-baseline gap-3 flex-wrap">
                  <div className="font-display text-3xl md:text-4xl text-navy">{office.region}</div>
                  {office.label === "Principal Offices" && (
                    <span className="text-[10px] tracking-[0.28em] uppercase text-oxblood font-sans">Principal</span>
                  )}
                </div>
                <div className="mt-3 text-xs uppercase tracking-[0.22em] text-slate2/60">{office.serves}</div>
                <div className="mt-6 text-slate2 font-light leading-relaxed">{office.line1}</div>
                <div className="text-slate2 font-light">{office.line2}</div>
                {office.note && (
                  <div className="mt-2 font-cormorant italic text-oxblood/80">{office.note}</div>
                )}

                <div className="mt-8 hairline pt-6 space-y-5">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-slate2/60">Principal Office · Frisco</div>
                    <a href={MAIN_OFFICE_PHONE.href} className="mt-1 block font-display text-2xl text-navy hover:text-oxblood transition" data-testid="location-phone-main-office">
                      {MAIN_OFFICE_PHONE.display}
                    </a>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-slate2/60">Toll-Free · Statewide</div>
                    <a href={FIRM_PHONE.href} className="mt-1 block font-display text-2xl text-navy hover:text-oxblood transition" data-testid="location-phone-main">
                      {FIRM_PHONE.display}
                    </a>
                  </div>
                  <div className="pt-3">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-oxblood">PEAK American Financial</div>
                    <div className="text-[9px] uppercase tracking-[0.22em] text-slate2/60">Clients &amp; Representatives Only</div>
                    <a href={PEAK_PHONE.href} className="mt-1 block font-cormorant italic text-2xl text-oxblood hover:text-navy transition" data-testid="location-phone-peak">
                      {PEAK_PHONE.display}
                    </a>
                  </div>
                </div>

                <div className="mt-10">
                  <Link to="/intake" className="btn-primary w-full text-center inline-block" data-testid="location-aside-intake">
                    Request a Conference
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* PRACTICE LINK */}
      <section className="bg-ivory" data-testid="location-practice">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24">
          <div className="kicker">Counsel Across the Continuum</div>
          <h3 className="mt-6 font-display text-4xl md:text-5xl leading-[1.05] max-w-3xl">
            The same six-stage practice — <span className="italic font-cormorant text-oxblood">delivered from {office.region}.</span>
          </h3>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-8">
            {PRACTICE_AREAS.map((p) => (
              <Link key={p.slug} to="/practice" className="group hairline-top pt-5" data-testid={`location-practice-${p.slug}`}>
                <div className="text-[10px] tracking-[0.32em] uppercase text-brass">{p.no}</div>
                <div className="mt-2 font-display text-2xl text-navy group-hover:text-oxblood transition">{p.title}</div>
                <div className="mt-1 font-cormorant italic text-sm text-slate2">{p.serif}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* OTHER OFFICES */}
      <section className="bg-ivory" data-testid="location-other-offices">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="kicker">Other Texas Offices</div>
          <h3 className="mt-6 font-display text-4xl md:text-5xl leading-[1.05] max-w-2xl">
            Statewide presence.<br/>
            <span className="italic font-cormorant text-oxblood">Local counsel.</span>
          </h3>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {otherOffices.map((o) => (
              <Link key={o.slug} to={`/locations/${o.slug}`} className="group" data-testid={`location-other-${o.slug}`}>
                <div className="aspect-[4/5] overflow-hidden bg-navy">
                  <img src={o.hero} alt={o.heroAlt} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                </div>
                <div className="mt-5 hairline-top pt-4">
                  <div className="text-[10px] tracking-[0.28em] uppercase text-brass">{o.kicker}</div>
                  <div className="mt-2 font-display text-2xl text-navy group-hover:text-oxblood transition">{o.region}</div>
                  <div className="mt-1 text-sm text-slate2 font-light">{o.serves}</div>
                  <div className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-oxblood font-medium">
                    Visit Office <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
